import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
    Plus,
    Search,
    Users,
    Building2,
    Mail,
    Phone,
    Star,
    MoreHorizontal,
} from "lucide-react";
import { getStatusColor, statusLabels, formatDate } from "../../lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Pagination } from "../../components/ui/pagination-control";

const initialFormData = {
    name: "",
    email: "",
    phone: "",
    address: "",
    industry: "",
    status: "prospect",
    notes: "",
    rating: null,
    payment_rating: null,
    communication_rating: null,
    collaboration_rating: null,
};

export const ClientsPage = () => {
    const navigate = useNavigate();
    const { get, post, del } = useApi();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 12;

    const fetchClients = useCallback(async () => {
        try {
            const data = await get("/clients");
            setClients(data);
        } catch (error) {
            toast.error("Erreur lors du chargement des clients");
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await post("/clients", formData);
            toast.success("Client créé avec succès");
            setDialogOpen(false);
            setFormData(initialFormData);
            fetchClients();
        } catch (error) {
            toast.error("Erreur lors de la création du client");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (clientId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) return;

        try {
            await del(`/clients/${clientId}`);
            toast.success("Client supprimé");
            fetchClients();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const filteredClients = clients.filter((client) => {
        const matchesSearch =
            client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            client.email?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === "all" || client.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredClients.length / PAGE_SIZE);
    const paginatedClients = filteredClients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const clientsByStatus = {
        prospect: paginatedClients.filter((c) => c.status === "prospect"),
        negotiation: paginatedClients.filter((c) => c.status === "negotiation"),
        active: paginatedClients.filter((c) => c.status === "active"),
        loyal: paginatedClients.filter((c) => c.status === "loyal"),
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="clients-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">CRM Clients</h1>
                    <p className="text-muted-foreground mt-1">{clients.length} clients au total</p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-client-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau client
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Nouveau client</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <Label htmlFor="name">Nom *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        data-testid="client-name-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        data-testid="client-email-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="phone">Téléphone</Label>
                                    <Input
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        data-testid="client-phone-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="industry">Secteur</Label>
                                    <Input
                                        id="industry"
                                        value={formData.industry}
                                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="status">Statut</Label>
                                    <Select
                                        value={formData.status}
                                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                                    >
                                        <SelectTrigger data-testid="client-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="prospect">Prospect</SelectItem>
                                            <SelectItem value="negotiation">En négociation</SelectItem>
                                            <SelectItem value="active">Client actif</SelectItem>
                                            <SelectItem value="loyal">Client fidèle</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2">
                                    <Label htmlFor="address">Adresse</Label>
                                    <Input
                                        id="address"
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label htmlFor="notes">Notes</Label>
                                    <Textarea
                                        id="notes"
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="submit-client-btn">
                                    {submitting ? "Création..." : "Créer le client"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher un client..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="search-clients-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48" data-testid="status-filter">
                        <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        <SelectItem value="prospect">Prospects</SelectItem>
                        <SelectItem value="negotiation">En négociation</SelectItem>
                        <SelectItem value="active">Clients actifs</SelectItem>
                        <SelectItem value="loyal">Clients fidèles</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Pipeline View */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Object.entries(clientsByStatus).map(([status, statusClients]) => (
                    <div key={status} className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Badge className={getStatusColor(status)}>{statusLabels[status]}</Badge>
                                <span className="text-sm text-muted-foreground font-mono">{statusClients.length}</span>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {statusClients.map((client) => (
                                <Card
                                    key={client.client_id}
                                    className="cursor-pointer hover:shadow-md transition-all duration-200"
                                    onClick={() => navigate(`/clients/${client.client_id}`)}
                                    data-testid={`client-card-${client.client_id}`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                                    <Building2 className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-sm">{client.name}</h4>
                                                    {client.industry && (
                                                        <p className="text-xs text-muted-foreground">{client.industry}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/clients/${client.client_id}`);
                                                    }}>
                                                        Voir détails
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className="text-destructive"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(client.client_id);
                                                        }}
                                                    >
                                                        Supprimer
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>

                                        <div className="mt-3 space-y-1.5">
                                            {client.email && (
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <Mail className="w-3 h-3" />
                                                    <span className="truncate">{client.email}</span>
                                                </div>
                                            )}
                                            {client.phone && (
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <Phone className="w-3 h-3" />
                                                    <span>{client.phone}</span>
                                                </div>
                                            )}
                                        </div>

                                        {client.rating && (
                                            <div className="flex items-center gap-1 mt-3">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star
                                                        key={i}
                                                        className={`w-3 h-3 ${
                                                            i < client.rating
                                                                ? "text-yellow-500 fill-yellow-500"
                                                                : "text-muted-foreground"
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}

                            {statusClients.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground text-sm border border-dashed rounded-lg">
                                    Aucun client
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <Pagination
                page={currentPage}
                pages={totalPages}
                total={filteredClients.length}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => setCurrentPage(p)}
            />
        </div>
    );
};
